import { useCallback } from 'react';
import { useNotification } from '@/hooks/useNotification';
import { getResourceDetails } from '../apiServices/resourceApi';
import { downloadFile } from '../util/UtilFunctions';
import type { NormalizedItem } from '@/components/itemList/types';

const FAVORITES_KEY = 'favouriteItems';

/**
 * Centralized item action logic.
 * Returns action functions that components can call.
 */
export function useItemActions(options: {
  updateIsQuerying: (querying: boolean) => void;
  onRefresh: () => void;
}) {
  const { updateIsQuerying, onRefresh } = options;
  const { notify } = useNotification();

  // ── Download ────────────────────────────────────────────────────

  const downloadItem = useCallback(async (item: NormalizedItem) => {
    const name = item.name || item.title;
    try {
      updateIsQuerying(true);
      let url = item.signedUrl;
      if (!url) {
        const details = await getResourceDetails(item.id);
        url = (details as { response: { Item: { signedUrl: { S: string } } } }).response.Item.signedUrl.S;
      }
      await downloadFile(name, url);
      notify({ id: String(Date.now()), type: 'success', message: 'Downloading file' });
    } catch {
      notify({ id: String(Date.now()), type: 'error', message: 'Could not download file' });
    } finally {
      updateIsQuerying(false);
    }
  }, [updateIsQuerying, notify]);

  const downloadItems = useCallback(async (items: NormalizedItem[]) => {
    for (const item of items) {
      await downloadItem(item);
    }
  }, [downloadItem]);

  // ── Favorites ───────────────────────────────────────────────────

  const isFavorite = useCallback((itemId: string): boolean => {
    try {
      const stored = localStorage.getItem(FAVORITES_KEY);
      if (!stored) return false;
      const arr = JSON.parse(stored) as string[];
      return arr.includes(itemId);
    } catch {
      return false;
    }
  }, []);

  const toggleFavorite = useCallback((item: NormalizedItem) => {
    try {
      const stored = localStorage.getItem(FAVORITES_KEY);
      const arr: string[] = stored ? JSON.parse(stored) : [];
      const index = arr.indexOf(item.id);
      if (index >= 0) {
        arr.splice(index, 1);
        notify({ id: String(Date.now()), type: 'success', message: 'Removed from favorites' });
      } else {
        arr.push(item.id);
        notify({ id: String(Date.now()), type: 'success', message: 'Added to favorites' });
      }
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(arr));
    } catch {
      // localStorage not available
    }
  }, [notify]);

  return {
    downloadItem,
    downloadItems,
    isFavorite,
    toggleFavorite,
  };
}
